import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

const TOKEN_KEY = "pag_access_token";

export function getToken() {
  try { return localStorage.getItem(TOKEN_KEY); } catch { return null; }
}

export function setToken(token) {
  try {
    if (token) localStorage.setItem(TOKEN_KEY, token);
    else localStorage.removeItem(TOKEN_KEY);
  } catch { /* ignore */ }
}

// --- JWT helpers (no dependency, just b64 decode of payload) ---
function decodeJwtExp(token) {
  try {
    const parts = token.split(".");
    if (parts.length !== 3) return 0;
    const payload = JSON.parse(atob(parts[1].replace(/-/g, "+").replace(/_/g, "/")));
    return typeof payload.exp === "number" ? payload.exp * 1000 : 0;
  } catch { return 0; }
}

const REFRESH_THRESHOLD_MS = 12 * 3600 * 1000;  // refresh when less than 12h left
let refreshInflight = null;  // de-dup concurrent refreshes

async function maybeRefreshSoon() {
  const t = getToken();
  if (!t) return;
  const exp = decodeJwtExp(t);
  if (!exp) return;
  const remaining = exp - Date.now();
  if (remaining > 0 && remaining < REFRESH_THRESHOLD_MS) {
    if (!refreshInflight) {
      refreshInflight = axios
        .post(`${API}/auth/refresh`, null, { headers: { Authorization: `Bearer ${t}` } })
        .then((r) => { if (r?.data?.access_token) setToken(r.data.access_token); })
        .catch(() => { /* silencieux — l'interceptor 401 gérera */ })
        .finally(() => { refreshInflight = null; });
    }
    return refreshInflight;
  }
}

export const api = axios.create({
  baseURL: API,
});

// Attach Bearer token automatically + opportunistic refresh
api.interceptors.request.use(async (config) => {
  // Skip refresh check on the refresh call itself to avoid recursion
  if (!config.url?.includes("/auth/refresh")) {
    try { await maybeRefreshSoon(); } catch { /* ignore */ }
  }
  const token = getToken();
  if (token) {
    config.headers = config.headers || {};
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Drop token on 401
api.interceptors.response.use(
  (r) => r,
  (error) => {
    if (error?.response?.status === 401) {
      const url = error.config?.url || "";
      // Don't wipe token for the /me call check itself - just propagate
      if (!url.endsWith("/auth/me")) setToken(null);
    }
    return Promise.reject(error);
  }
);

export function formatApiError(detail) {
  if (detail == null) return "Une erreur est survenue.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail
      .map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e)))
      .filter(Boolean)
      .join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}
